import React, { useState, useMemo } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { VendorStatementDoc } from '../types';
import { VendorStatementJobRow } from './VendorStatementJobRow';

interface VendorStatementJobsTableProps {
  jobs: VendorStatementDoc[];
  title: string;
  isLoading: boolean;
  users: Record<string, string>;
  onJobClick: (job: VendorStatementDoc) => void;
}

/**
 * Table to display vendor statements jobs
 * @param jobs - The jobs to display
 * @param title - The title of the table
 * @param isLoading - Whether the table is loading
 * @param users - The users to display
 * @param onJobClick - The function to call when a job is clicked
 * @constructor
 */
// eslint-disable-next-line import/prefer-default-export
export const VendorStatementJobsTable: React.FC<VendorStatementJobsTableProps> = function ({
  jobs,
  title,
  isLoading,
  users,
  onJobClick,
}) {
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Paginate the jobs
  const paginatedJobs = useMemo(
    () => jobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [jobs, page, rowsPerPage],
  );

  // Show loading indicator if the table is loading
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Show the title of the table */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {title}
        {' '}
        (
        {jobs.length}
        )
      </Typography>

      {/* Table container */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Job ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Completed At</TableCell>
              <TableCell>Completed By</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No jobs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedJobs.map((job) => (
                <VendorStatementJobRow
                  key={job.id}
                  job={job}
                  users={users}
                  onJobClick={onJobClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Table pagination */}
      <TablePagination
        component="div"
        count={jobs.length}
        page={page}
        onPageChange={(_: unknown, newPage: number) => { setPage(newPage); }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};
